<script setup>
// Quan ly nhan vien: gat de ngung nguoi da nghi (chan dang nhap, giu lich su). SETUP.md 2.25.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import NhanVienAPI from 'dashboard/api/bonboz/nhanVien';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const store = useStore();

const agents = useMapGetter('agents/getAgents');
const currentUser = useMapGetter('getCurrentUser');
const accountId = useMapGetter('getCurrentAccountId');

const dialogRef = ref(null);
const xacNhanRef = ref(null);
const tuKhoa = ref('');
const dangNgung = ref(null);
const dangGui = ref(false);

const daNghi = agent => Boolean(agent.custom_attributes?.bonboz_deactivated_at);

// Nguoi dang lam len truoc, nguoi da nghi xuong cuoi.
const danhSach = computed(() => {
  const q = tuKhoa.value.trim().toLowerCase();
  return agents.value
    .filter(
      a =>
        !q ||
        a.name?.toLowerCase().includes(q) ||
        a.email?.toLowerCase().includes(q)
    )
    .sort((a, b) => daNghi(a) - daNghi(b));
});

const soDaNghi = computed(() => agents.value.filter(daNghi).length);

const goi = async (hanhDong, agent, thongBao) => {
  dangGui.value = true;
  try {
    await NhanVienAPI[hanhDong](agent.id);
    await store.dispatch('agents/get');
    useAlert(t(thongBao, { name: agent.name }));
  } catch (error) {
    useAlert(parseAPIErrorResponse(error));
  } finally {
    dangGui.value = false;
  }
};

// Ngung thi hoi lai; bat lai thi lam luon vi khong mat gi.
const doi = (agent, dangLam) => {
  if (dangLam) {
    goi('batLai', agent, 'BONBOZ.QUAN_LY_NHAN_VIEN.DA_BAT_LAI');
    return;
  }
  dangNgung.value = agent;
  xacNhanRef.value.open();
};

const xacNhanNgung = async () => {
  await goi('ngung', dangNgung.value, 'BONBOZ.QUAN_LY_NHAN_VIEN.DA_NGUNG');
  xacNhanRef.value.close();
};

const open = () => {
  tuKhoa.value = '';
  store.dispatch('agents/get');
  dialogRef.value.open();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="xl"
    :title="t('BONBOZ.QUAN_LY_NHAN_VIEN.TIEU_DE')"
    :description="
      t('BONBOZ.QUAN_LY_NHAN_VIEN.DEM', {
        dangLam: agents.length - soDaNghi,
        daNghi: soDaNghi,
      })
    "
    :show-confirm-button="false"
    :cancel-button-label="t('BONBOZ.QUAN_LY_NHAN_VIEN.DONG')"
    overflow-y-auto
  >
    <div class="flex flex-col gap-3 min-h-[16rem]">
      <input
        v-model="tuKhoa"
        type="search"
        class="reset-base w-full !h-9 !m-0 !text-sm px-3 rounded-lg outline outline-1 outline-[rgb(var(--pk-vien))] bg-n-background text-n-slate-12 placeholder:text-n-slate-10 focus-visible:outline-2 focus-visible:outline-n-brand"
        :placeholder="t('BONBOZ.QUAN_LY_NHAN_VIEN.TIM')"
        :aria-label="t('BONBOZ.QUAN_LY_NHAN_VIEN.TIM')"
      />

      <ul class="flex flex-col m-0 list-none divide-y divide-n-weak">
        <li
          v-for="agent in danhSach"
          :key="agent.id"
          class="flex gap-3 items-center py-2.5"
          :class="{ 'opacity-60': daNghi(agent) }"
        >
          <Avatar
            :src="agent.thumbnail"
            :name="agent.name"
            :size="32"
            rounded-full
            class="shrink-0"
          />
          <span class="flex-1 min-w-0">
            <span class="block text-sm font-medium truncate text-n-slate-12">
              {{ agent.name }}
            </span>
            <span class="block text-xs truncate text-n-slate-11">
              {{ agent.email }}
            </span>
          </span>
          <span
            class="text-xs font-medium px-2 py-0.5 rounded-full shrink-0"
            :class="
              daNghi(agent)
                ? 'bg-n-alpha-2 text-n-slate-11'
                : 'bg-n-teal-3 text-n-teal-11'
            "
          >
            {{
              daNghi(agent)
                ? t('BONBOZ.QUAN_LY_NHAN_VIEN.DA_NGHI')
                : t('BONBOZ.QUAN_LY_NHAN_VIEN.DANG_LAM')
            }}
          </span>
          <!-- Khong cho tu ngung chinh minh — server cung chan. -->
          <span
            v-if="agent.id === currentUser.id"
            class="w-7 text-xs text-center shrink-0 text-n-slate-10"
          >
            {{ t('BONBOZ.QUAN_LY_NHAN_VIEN.BAN') }}
          </span>
          <Switch
            v-else
            :model-value="!daNghi(agent)"
            :aria-label="
              t(
                daNghi(agent)
                  ? 'BONBOZ.QUAN_LY_NHAN_VIEN.BAT_LAI_NHAN'
                  : 'BONBOZ.QUAN_LY_NHAN_VIEN.NGUNG_NHAN',
                { name: agent.name }
              )
            "
            :disabled="dangGui"
            @update:model-value="value => doi(agent, value)"
          />
        </li>
      </ul>
      <p v-if="!danhSach.length" class="text-sm text-n-slate-11">
        {{ t('BONBOZ.QUAN_LY_NHAN_VIEN.TRONG') }}
      </p>

      <router-link
        :to="{ name: 'agent_list', params: { accountId } }"
        class="text-sm text-n-brand hover:underline"
        @click="dialogRef.close()"
      >
        {{ t('BONBOZ.QUAN_LY_NHAN_VIEN.CAI_DAT_DAY_DU') }}
      </router-link>
    </div>
  </Dialog>

  <Dialog
    ref="xacNhanRef"
    type="alert"
    :title="
      t('BONBOZ.QUAN_LY_NHAN_VIEN.XAC_NHAN_TIEU_DE', { name: dangNgung?.name })
    "
    :confirm-button-label="t('BONBOZ.QUAN_LY_NHAN_VIEN.XAC_NHAN_NUT')"
    :is-loading="dangGui"
    @confirm="xacNhanNgung"
  >
    <template #description>
      <ul class="m-0 text-sm list-disc ps-5 text-n-slate-11">
        <li>{{ t('BONBOZ.QUAN_LY_NHAN_VIEN.HAU_QUA.DANG_XUAT') }}</li>
        <li>{{ t('BONBOZ.QUAN_LY_NHAN_VIEN.HAU_QUA.HOI_THOAI') }}</li>
        <li>{{ t('BONBOZ.QUAN_LY_NHAN_VIEN.HAU_QUA.LICH_SU') }}</li>
      </ul>
    </template>
  </Dialog>
</template>
